import { useEffect, useRef, useState } from 'react';

/**
 * Fondo "CrimsonNebula" del laboratorio de componentes
 * (Apps_Growing/react-ui-lab/src/lib/backgrounds/CrimsonNebula.jsx):
 * nebulosa con cintas de latido y el centro despejado. Misma forma que el
 * original, pintada con los morados profundos de FacturaGS en vez de rojo.
 *
 * Es el mismo shader, pero sobre WebGL directo en vez de three.js +
 * @react-three/fiber: esas librerias piden React 19 y sumaban cerca de
 * 600 KB al sistema solo por el fondo de la entrada.
 *
 * Cuida la maquina de la caja: dibuja a 3/4 de resolucion (la nebulosa
 * es difusa y se ve igual), a unos 30 cuadros por segundo, se detiene con
 * la pestana oculta y suelta la GPU al entrar al sistema. Sin WebGL no
 * dibuja nada y queda el degradado de respaldo del CSS (.lg-escena).
 */

const VERTICES = `
  attribute vec2 aPos;
  varying vec2 vUv;
  void main() {
    vUv = aPos * 0.5 + 0.5;
    gl_Position = vec4(aPos, 0.0, 1.0);
  }
`;

const FRAGMENTOS = `
  #ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
  #else
  precision mediump float;
  #endif
  uniform float uTime;
  uniform float uSpeed;
  uniform float uAspect;
  varying vec2 vUv;

  float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p){
    vec2 i = floor(p); vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
               mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
  }
  float fbm(vec2 p){
    float v = 0.0, a = 0.5;
    mat2 m = mat2(1.6, 1.2, -1.2, 1.6);
    for (int i = 0; i < 5; i++){ v += a * noise(p); p = m * p; a *= 0.5; }
    return v;
  }
  // Cinta brillante: campo de distancia a una linea curva. Igual al
  // original, escrito como 1 - smoothstep(0, e, d) porque smoothstep con
  // los bordes invertidos no esta definido en todas las tarjetas de video.
  float ribbon(vec2 p, float seed, float t){
    float wave = sin(p.x * 2.5 + seed + t * 0.4) * 0.12
               + sin(p.x * 5.1 - seed * 0.7 + t * 0.25) * 0.055;
    float dist = abs(p.y - wave);
    return 1.0 - smoothstep(0.0, 0.025, dist);
  }

  void main(){
    vec2 uv = vUv - 0.5;
    uv.x *= uAspect;               // el original fijaba 16:9
    float t = uTime * 0.08 * uSpeed;

    // Nebulosa con domain warping
    vec2 q = vec2(fbm(uv * 2.2 + t),
                  fbm(uv * 2.2 + vec2(5.2, 1.3) + t));
    vec2 r = vec2(fbm(uv * 3.0 + 3.0*q + vec2(1.7, 9.2) + t * 0.6),
                  fbm(uv * 3.0 + 3.0*q + vec2(8.3, 2.8) + t * 0.5));
    float f = fbm(uv * 2.5 + 3.5*r + t * 0.3);

    // Morados de la marca: medianoche, #4c1d95, #5b21b6 y #7c3aed
    vec3 col = vec3(0.0);
    col = mix(col, vec3(0.07, 0.02, 0.17), smoothstep(0.0,  0.6,  f));
    col = mix(col, vec3(0.22, 0.07, 0.48), smoothstep(0.3,  0.65, f));
    col = mix(col, vec3(0.42, 0.16, 0.86), smoothstep(0.52, 0.62, f * f));

    // Cintas tipo latido
    float r1 = ribbon(uv + vec2(0.0, 0.07), 0.0, t);
    float r2 = ribbon(uv + vec2(0.0,-0.05), 2.1, t * 0.85);
    float r3 = ribbon(uv + vec2(0.0, 0.20), 4.7, t * 1.15);
    float rAll = max(max(r1, r2), r3);
    col += vec3(0.58, 0.34, 1.00) * rAll * 1.2;

    // Nodos brillantes en los cruces
    float node = r1 * r2 * 40.0;
    col += vec3(0.86, 0.78, 1.0) * clamp(node, 0.0, 1.0);

    // La tarjeta es cristal: el centro se baja apenas para leer el texto,
    // sin apagar la nebulosa que se tiene que ver detras.
    float centerMask = smoothstep(0.0, 0.85, length(uv));
    col *= 0.78 + 0.22 * centerMask;
    col *= 0.94;
    float vign = 1.0 - smoothstep(0.35, 0.95, length(uv * vec2(0.9, 1.2)));
    col *= 0.55 + 0.45 * vign;

    gl_FragColor = vec4(col, 1.0);
  }
`;

const ESCALA = 0.75;          // fraccion de la resolucion real que se dibuja
const PASO_MS = 1000 / 30;    // ~30 cuadros por segundo
const CUADRO_QUIETO = 20;     // segundo que se muestra con "reducir movimiento"

function compilar(gl, tipo, fuente) {
  const shader = gl.createShader(tipo);
  gl.shaderSource(shader, fuente);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function crearPrograma(gl) {
  const vs = compilar(gl, gl.VERTEX_SHADER, VERTICES);
  const fs = compilar(gl, gl.FRAGMENT_SHADER, FRAGMENTOS);
  if (!vs || !fs) return null;
  const programa = gl.createProgram();
  gl.attachShader(programa, vs);
  gl.attachShader(programa, fs);
  gl.linkProgram(programa);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(programa, gl.LINK_STATUS)) {
    gl.deleteProgram(programa);
    return null;
  }
  return programa;
}

export default function FondoNebulosa({ velocidad = 1 }) {
  const cajaRef = useRef(null);
  const [lista, setLista] = useState(false);

  useEffect(() => {
    // El lienzo se crea aca y no en el JSX: cada montaje tiene el suyo, asi
    // que al desmontar se puede soltar la GPU sin romper un segundo montaje
    // (React en desarrollo monta dos veces).
    const caja = cajaRef.current;
    const canvas = document.createElement('canvas');
    caja.appendChild(canvas);

    const gl = canvas.getContext('webgl', {
      alpha: false, antialias: false, depth: false, powerPreference: 'low-power',
    });
    const programa = gl && crearPrograma(gl);
    if (!programa) {
      canvas.remove();
      return undefined;
    }

    gl.useProgram(programa);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    // Un solo triangulo que cubre toda la pantalla
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(programa, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
    const uTime = gl.getUniformLocation(programa, 'uTime');
    const uAspect = gl.getUniformLocation(programa, 'uAspect');
    gl.uniform1f(gl.getUniformLocation(programa, 'uSpeed'), velocidad);

    const ajustar = () => {
      const densidad = Math.min(window.devicePixelRatio || 1, 1.5) * ESCALA;
      const w = Math.max(1, Math.round(canvas.clientWidth * densidad));
      const h = Math.max(1, Math.round(canvas.clientHeight * densidad));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
      gl.uniform1f(uAspect, w / h);
    };
    const dibujar = (segundos) => {
      gl.uniform1f(uTime, segundos);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    // Con "reducir movimiento" se pinta un solo cuadro y queda quieta.
    const quieta = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const inicio = performance.now();
    let raf = 0;
    let ultimo = 0;
    const animar = (ahora) => {
      raf = requestAnimationFrame(animar);
      if (ahora - ultimo < PASO_MS) return;
      ultimo = ahora;
      dibujar((ahora - inicio) / 1000);
    };

    ajustar();
    dibujar(quieta ? CUADRO_QUIETO : 0);
    setLista(true);
    if (!quieta) raf = requestAnimationFrame(animar);

    const alRedimensionar = () => {
      ajustar();
      if (quieta) dibujar(CUADRO_QUIETO);
    };
    const alCambiarVisibilidad = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
        raf = 0;
      } else if (!quieta && !raf) {
        raf = requestAnimationFrame(animar);
      }
    };
    const alPerderContexto = (e) => {
      e.preventDefault();
      cancelAnimationFrame(raf);
      raf = 0;
      setLista(false);
    };
    window.addEventListener('resize', alRedimensionar);
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    canvas.addEventListener('webglcontextlost', alPerderContexto);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', alRedimensionar);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      canvas.removeEventListener('webglcontextlost', alPerderContexto);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(programa);
      // Al entrar al sistema la GPU queda libre para el resto del trabajo.
      gl.getExtension('WEBGL_lose_context')?.loseContext();
      canvas.remove();
    };
  }, [velocidad]);

  return <div ref={cajaRef} className={lista ? 'lg-nebula lista' : 'lg-nebula'} aria-hidden="true" />;
}
